/**
 * Fortschrittskarte eines Hintergrund-Auftrags im Chat. (19.10.2026, Hintergrund-Fach Etappe 2, shared/job-fortschritt.ts)
 *
 * EINE Karte je Auftrag (Schlüssel: Kennung) — ein zweites Ereignis zum selben Auftrag aktualisiert sie, legt nie eine zweite an.
 * Sie zeigt Titel, Laufzeit, letzte Statuszeile und „Abbrechen“ (vorhandene Route /api/background-tasks/:id/cancel). Das Ende
 * (done/failed) ergänzt die Karte um eine ruhige Schlusszeile ohne Knopf; das Ergebnis selbst kommt wie bisher als Chat-Zeile.
 * Nach ERLEDIGT_SICHTBAR_MS verschwindet die Schlusskarte von selbst.
 *
 * ⚠️ Kein Chat-Beitrag: kein `.bubble`, kein `data-ts` — Verlauf, Zähler und Doppel-Erkennung sehen die Karte nicht. Sie steht per CSS
 *    `order` am Ende des Verlaufs (wie die Warteblase), wird also nie umgehängt.
 * ⚠️ Alle Texte nur über textContent. Die Laufzeit rechnet die Seite aus ihrer eigenen Uhr (`laufMs` kommt vom Server als Dauer, nie als Zeitpunkt).
 * ⚠️ Barrierefreiheit: Gruppe mit Beschriftung und `aria-live="off"` (#chat ist polite — Laufzeit und Statuszeilen werden nicht vorgelesen);
 *    erst die Schlusszeile am Ende ist `role="status"`; Knopf 44 Punkte hoch (CSS).
 *
 * 27.10.2026 (Etappen-Lauf, server/etappen-lauf.ts): dieselbe Karte zeigt einen Prozess im Hintergrund (`art: "prozess"`) — Titel „Ein Prozess
 * läuft (Schritt n)“, Knopf „Stopp“. Der Stopp geht an /api/chat/abort mit `{ stopp: true }` (der EINE ausdrückliche Stopp; eine neue
 * Nachricht stoppt den Prozess nicht).
 */
import { laufzeitText, type JobFortschritt } from "../../shared/job-fortschritt.js";

export const JOB_HOST_KLASSE = "job-karten";
export const JOB_KARTE_KLASSE = "job-karte";
/** Eine beendete Karte bleibt so lange stehen (ms). */
export const ERLEDIGT_SICHTBAR_MS = 2 * 60_000;
/** So lange nach einem Neuverbinden wartet die Seite auf den nachgelieferten Stand, bevor sie eine stumme Karte entfernt. */
export const VERBINDUNG_NEU_FRIST_MS = 5000;

/** Bricht einen Auftrag ab; `true` = der Server hat angenommen. `art`: „prozess“ = Etappen-Lauf (eigener Stopp-Weg). */
export type JobAbbrecher = (id: string, art?: JobFortschritt["art"]) => Promise<boolean> | boolean;

/** Der ausdrückliche Stopp eines Prozesses (Etappen-Lauf) — derselbe Weg wie der Stopp-Knopf bei leerem Feld. */
export async function sendeProzessStopp(): Promise<boolean> {
  try {
    const r = await fetch("/api/chat/abort", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ stopp: true }),
    });
    return r.ok;
  } catch {
    return false;
  }
}

export const sendeJobAbbruch: JobAbbrecher = async (id, art) => {
  if (art === "prozess") return sendeProzessStopp();
  try {
    const r = await fetch(`/api/background-tasks/${encodeURIComponent(id)}/cancel`, { method: "POST", credentials: "same-origin" });
    return r.ok;
  } catch {
    return false;
  }
};

/** Der Knopftext: „Stopp“ für einen Prozess, „Abbrechen“ für einen Hintergrund-Auftrag. */
const knopfText = (job: JobFortschritt): string => (job.art === "prozess" ? "Stopp" : "Abbrechen");

interface Eintrag {
  el: HTMLElement;
  /** Beginn nach der Uhr dieser Seite. */
  start: number;
  job: JobFortschritt;
  /** In welcher Verbindungs-Runde zuletzt ein Stand kam (siehe verbindungNeu). */
  runde: number;
  weg: ReturnType<typeof setTimeout> | null;
}

export interface JobAnzeige {
  setze(job: JobFortschritt): void;
  /**
   * Der Strom hat sich neu verbunden: der Server liefert die laufenden Aufträge gleich nach „ready“ nach. Eine laufende Karte, zu der
   * danach (VERBINDUNG_NEU_FRIST_MS) kein Stand kommt, endete während des Abrisses und wird entfernt — sie bleibt nie hängen.
   */
  verbindungNeu(): void;
  /** Wie viele Karten stehen (laufende und beendete). */
  anzahl(): number;
  /** Test: Zeitgeber aufräumen. */
  stopp(): void;
}

function schluss(job: JobFortschritt): string {
  const dauer = laufzeitText(job.dauerMs ?? job.laufMs);
  if (job.art === "prozess") return job.status === "done" ? `Prozess fertig · ${dauer} · Ergebnis steht im Chat` : `Prozess beendet · ${dauer}`;
  return job.status === "done" ? `fertig · ${dauer}${job.imChat ? " · Ergebnis steht im Chat" : ""}` : `abgebrochen oder fehlgeschlagen · ${dauer}`;
}

export function baueJobAnzeige(
  wurzel: HTMLElement,
  abbrechen: JobAbbrecher = sendeJobAbbruch,
  jetzt: () => number = Date.now,
): JobAnzeige {
  const doc = wurzel.ownerDocument;
  const eintraege = new Map<string, Eintrag>();
  let host: HTMLElement | null = null;
  let takt: ReturnType<typeof setInterval> | null = null;
  let runde = 0;

  const zeitAktualisieren = (e: Eintrag): void => {
    const z = e.el.querySelector<HTMLElement>(".job-zeit");
    if (z) z.textContent = e.job.status === "running" ? laufzeitText(jetzt() - e.start) : "";
  };
  const taktPruefen = (): void => {
    const laeuft = [...eintraege.values()].some((e) => e.job.status === "running");
    if (laeuft && !takt) {
      takt = setInterval(() => {
        for (const e of eintraege.values()) if (e.job.status === "running") zeitAktualisieren(e);
      }, 1000);
    } else if (!laeuft && takt) {
      clearInterval(takt);
      takt = null;
    }
  };
  const hostSichern = (): HTMLElement => {
    if (!host) {
      host = doc.createElement("div");
      host.className = JOB_HOST_KLASSE;
    }
    if (!host.isConnected) wurzel.appendChild(host);
    return host;
  };
  const entferne = (id: string): void => {
    const e = eintraege.get(id);
    if (!e) return;
    if (e.weg) clearTimeout(e.weg);
    e.el.remove();
    eintraege.delete(id);
    if (eintraege.size === 0) {
      host?.remove();
      host = null;
    }
    taktPruefen();
  };

  const baue = (job: JobFortschritt): HTMLElement => {
    const karte = doc.createElement("section");
    karte.className = `${JOB_KARTE_KLASSE} chat-karte`;
    karte.dataset.id = job.id;
    if (job.art) karte.dataset.art = job.art;
    karte.setAttribute("role", "group");
    // #chat ist aria-live="polite": die tickende Karte darf nicht vorgelesen werden — erst das Ende (role=status an der Schlusszeile).
    karte.setAttribute("aria-live", "off");
    karte.setAttribute("aria-label", job.art === "prozess" ? "Prozess im Hintergrund" : `Hintergrund-Auftrag: ${job.titel}`);

    const kopf = doc.createElement("div");
    kopf.className = "job-kopf";
    const punkt = doc.createElement("span");
    punkt.className = "job-punkt";
    punkt.setAttribute("aria-hidden", "true");
    const titel = doc.createElement("h3");
    titel.className = "job-titel";
    const zeit = doc.createElement("span");
    zeit.className = "job-zeit";
    zeit.setAttribute("aria-hidden", "true");
    kopf.append(punkt, titel, zeit);

    const zeile = doc.createElement("p");
    zeile.className = "job-zeile";

    const knopf = doc.createElement("button");
    knopf.type = "button";
    knopf.className = "job-abbrechen";
    knopf.textContent = knopfText(job);
    if (job.art === "prozess") knopf.setAttribute("aria-label", "Prozess stoppen");
    knopf.addEventListener("click", () => {
      if (karte.dataset.bricht === "1") return;
      karte.dataset.bricht = "1";
      knopf.disabled = true;
      knopf.textContent = job.art === "prozess" ? "Stoppe …" : "Breche ab …";
      void Promise.resolve(abbrechen(job.id, job.art)).then(
        (ok) => {
          if (ok) return; // der Server meldet das Ende über den Strom
          wieder();
        },
        () => wieder(),
      );
    });
    const wieder = (): void => {
      delete karte.dataset.bricht;
      knopf.disabled = false;
      knopf.textContent = knopfText(job);
      zeile.textContent = job.art === "prozess" ? "Das Stoppen hat nicht geklappt — tipp noch einmal auf Stopp." : "Das Abbrechen hat nicht geklappt — versuch es noch einmal.";
    };

    karte.append(kopf, zeile, knopf);
    return karte;
  };

  return {
    setze(job) {
      let e = eintraege.get(job.id);
      if (!e) {
        e = { el: baue(job), start: jetzt() - job.laufMs, job, runde, weg: null };
        eintraege.set(job.id, e);
        hostSichern().appendChild(e.el);
      }
      // Ein Ende ist endgültig: ein verspätetes „running“ weckt die Karte nicht wieder auf.
      if (e.job.status !== "running" && job.status === "running") return;
      e.job = job;
      e.runde = runde;
      const el = e.el;
      el.dataset.status = job.status;
      const titel = el.querySelector<HTMLElement>(".job-titel");
      if (titel) titel.textContent = job.titel;
      const zeile = el.querySelector<HTMLElement>(".job-zeile");
      const knopf = el.querySelector<HTMLButtonElement>(".job-abbrechen");
      if (job.status === "running") {
        if (zeile && el.dataset.bricht !== "1" && zeile.textContent !== (job.zeile ?? "läuft …")) zeile.textContent = job.zeile ?? "läuft …";
        if (knopf) knopf.hidden = false;
        // Die Laufzeit bleibt an EINEM Beginn hängen; ein späterer Stand korrigiert ihn nur, wenn die Uhren stark abweichen.
        const soll = jetzt() - job.laufMs;
        if (Math.abs(soll - e.start) > 5000) e.start = soll;
      } else {
        if (zeile) {
          zeile.setAttribute("role", "status");
          zeile.textContent = schluss(job);
        }
        if (knopf) knopf.hidden = true;
        delete el.dataset.bricht;
        if (!e.weg) e.weg = setTimeout(() => entferne(job.id), ERLEDIGT_SICHTBAR_MS);
      }
      zeitAktualisieren(e);
      taktPruefen();
    },
    verbindungNeu() {
      runde += 1;
      const diese = runde;
      setTimeout(() => {
        for (const [id, e] of [...eintraege]) if (e.job.status === "running" && e.runde < diese) entferne(id);
      }, VERBINDUNG_NEU_FRIST_MS);
    },
    anzahl: () => eintraege.size,
    stopp() {
      if (takt) clearInterval(takt);
      takt = null;
      for (const e of eintraege.values()) if (e.weg) clearTimeout(e.weg);
    },
  };
}
